import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'

/**
 * Tracks how many modals are currently open app-wide.
 *
 * Why a count (not a boolean): modals can stack (e.g. a confirm dialog on top of
 * another modal). A simple true/false would flip off when the top one closes even
 * if another modal is still open underneath.
 *
 * Consumers (e.g. GalleryMobile) use `useIsAnyModalOpen` to pause competing
 * keyboard handlers while any modal is visible.
 */

type ModalOpenContextValue = {
	/** Number of registered open modal instances. */
	openCount: number
	/** Called when a modal becomes visible. */
	increment: () => void
	/** Called when a modal closes or unmounts while open. Clamped at 0. */
	decrement: () => void
}

const ModalOpenContext = createContext<ModalOpenContextValue | null>(null)

/** Mount once near the app root (see AppLayout) so all Modals share one open count. */
export const ModalOpenProvider = ({ children }: PropsWithChildren) => {
	const [openCount, setOpenCount] = useState(0)

	const increment = useCallback(() => {
		setOpenCount((count) => count + 1)
	}, [])

	const decrement = useCallback(() => {
		// Math.max guards against mismatched register/unregister (e.g. Strict Mode edge cases).
		setOpenCount((count) => Math.max(0, count - 1))
	}, [])

	const value = useMemo(
		() => ({
			openCount,
			increment,
			decrement
		}),
		[openCount, increment, decrement]
	)

	return <ModalOpenContext.Provider value={value}>{children}</ModalOpenContext.Provider>
}

const useModalOpenContext = () => {
	const context = useContext(ModalOpenContext)

	if (!context) {
		throw new Error('ModalOpenContext is missing. Wrap the app with ModalOpenProvider.')
	}

	return context
}

/**
 * Registers this modal instance in the open-count while `show` is true.
 *
 * Wired from `Modal` via `useRegisterModalOpen(show)`.
 *
 * How open → close works (React effect lifecycle):
 * 1. `isOpen` becomes true → effect runs → `increment()` → returns a cleanup fn
 * 2. `isOpen` becomes false (or deps change / unmount) → React runs that cleanup
 *    first → `decrement()` → then the new effect body runs and early-returns
 *
 * So close does call `decrement()` — just not in the `if (!isOpen)` branch. That
 * branch only skips registering when already closed; the previous cleanup owns
 * the decrement.
 */
export const useRegisterModalOpen = (show: boolean | undefined) => {
	const { increment, decrement } = useModalOpenContext()
	const isOpen = Boolean(show)

	useEffect(() => {
		// Already closed: nothing to register. (If we were open before, React
		// already ran the previous effect's cleanup → decrement — see above.)
		if (!isOpen) {
			return undefined
		}

		increment()

		// Cleanup runs when isOpen flips to false, deps change, or the component unmounts.
		return () => {
			decrement()
		}
	}, [isOpen, increment, decrement])
}

/** True when at least one registered modal is open. Prefer this over reading `openCount` directly. */
export const useIsAnyModalOpen = () => {
	const { openCount } = useModalOpenContext()

	return openCount > 0
}
